import type { ReactNode } from 'react';
import { forwardRef, useEffect, useState, useCallback, useRef } from 'react';

import * as styles from './MessageToast.css';

export interface MessageToastProps {
  /**
   * The main message content to display
   */
  message: string;

  /**
   * Optional secondary message (smaller text below main message)
   */
  subMessage?: string;

  /**
   * Optional icon to display on the left side
   */
  icon?: ReactNode;

  /**
   * Visual variant for different toast types
   * @default 'default'
   */
  variant?: 'default' | 'error' | 'warning' | 'success' | 'info';

  /**
   * Whether the toast is visible
   * @default true
   */
  isVisible?: boolean;

  /**
   * Callback when the close button is clicked
   */
  onClose?: () => void;

  /**
   * Auto-dismiss timeout in milliseconds (0 = no auto-dismiss)
   * @default 5000
   */
  autoDismissTimeout?: number;

  /**
   * Optional ARIA label for accessibility
   */
  ariaLabel?: string;

  /**
   * Additional CSS class names
   */
  className?: string;
}

export const MessageToast = forwardRef<HTMLDivElement, MessageToastProps>(
  // eslint-disable-next-line complexity
  ({ message, subMessage, icon, variant = 'default', isVisible = true, onClose, autoDismissTimeout = 5000, ariaLabel, className, ...props }, ref) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(isVisible);
    const [isDismissing, setIsDismissing] = useState(false);
    const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Handle dismiss animation
    const handleDismiss = useCallback(() => {
      if (isDismissing) return; // Prevent multiple dismiss calls

      setIsDismissing(true);
      setIsAnimating(false);

      // Wait for animation to complete, then call onClose
      dismissTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsDismissing(false);
        onClose?.();
      }, 300); // Match animation duration
    }, [isDismissing, onClose]);

    // Cleanup timer on unmount
    useEffect(
      () => () => {
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
      },
      []
    );

    // Auto-dismiss functionality
    useEffect(() => {
      if (isVisible && autoDismissTimeout > 0 && onClose) {
        const timer = setTimeout(() => {
          handleDismiss();
        }, autoDismissTimeout);

        return () => clearTimeout(timer);
      }
    }, [isVisible, autoDismissTimeout, onClose, handleDismiss]);

    // Handle visibility changes with animation
    useEffect(() => {
      if (isVisible) {
        // Show: render immediately, then animate in
        setShouldRender(true);
        const timer = setTimeout(() => {
          setIsAnimating(true);
        }, 50);

        return () => clearTimeout(timer);
      } else {
        // Hide: animate out, then stop rendering
        setIsAnimating(false);
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 300); // Match animation duration

        return () => clearTimeout(timer);
      }
    }, [isVisible]);

    // Don't render if not visible and not animating
    if (!shouldRender) {
      return null;
    }

    // Build CSS classes
    const toastClasses = [styles.messageToast, styles.variants[variant], className].filter(Boolean).join(' ');

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent event bubbling
      handleDismiss();
    };

    return (
      <div className={`${styles.toastContainer} ${styles.responsiveContainer}`}>
        <div
          ref={ref}
          aria-label={ariaLabel || `Toast notification: ${message}`}
          aria-live="polite"
          className={toastClasses}
          role="alert"
          style={{
            transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
            opacity: isAnimating ? 1 : 0,
            transition: 'all 0.3s ease-out',
          }}
          {...props}
        >
          {/* Optional icon */}
          {icon && <div className={styles.iconContainer}>{icon}</div>}

          {/* Content */}
          <div className={styles.contentContainer}>
            <div className={styles.messageText}>{message}</div>
            {subMessage && <div className={styles.subMessageText}>{subMessage}</div>}
          </div>

          {/* Close button */}
          {onClose && (
            <button aria-label="Close notification" className={styles.closeButton} type="button" onClick={handleClose}>
              <svg fill="none" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }
);

MessageToast.displayName = 'MessageToast';
